// Public pricing page at /pricing on the marketing site. Static content only.
import { consolePath, contactMailto, docsHome } from "../lib/site";
import { SimulatedMoney, SiteFooter, SiteNav } from "./Landing";

const CONTACT = contactMailto("Game Banking Engine for my studio");

const plans: {
  name: string;
  price: string;
  per?: string;
  blurb: string;
  items: string[];
  cta: { label: string; href: string };
  featured?: boolean;
  tag?: string;
}[] = [
  {
    name: "Free",
    price: "$0",
    per: "sandbox forever, live during the beta",
    blurb: "The whole engine: build with test money, then run your live game.",
    items: [
      "Every API feature: banks, accounts, transfers, fees, cards, savings, fixed deposits, loans with credit scores and webhooks",
      "Unlimited games, sandbox and production keys",
      "The developer dashboard, browser playground and game clock",
      "All five SDKs",
      "Fair-use rate limits per API key",
    ],
    cta: { label: "Create a developer account", href: consolePath("/signup") },
  },
  {
    name: "Pro",
    price: "$10",
    per: "a month per developer account",
    blurb: "Dashboard extras for watching your economy as it runs.",
    items: [
      "Everything in Free",
      "Live balances: player and bank balances update on the dashboard the moment money moves",
      "A feed of the latest balance changes, in sandbox and live",
      "New Pro features as they ship, at no extra cost",
      "14-day free trial, no card needed",
    ],
    cta: { label: "Start your free trial", href: consolePath("/signup") },
    featured: true,
    tag: "New",
  },
  {
    name: "Studio",
    price: "Let's talk",
    blurb: "For large games, launches and live-ops teams.",
    items: [
      "Everything in Pro",
      "Higher rate limits for launches and events",
      "Help designing and migrating your economy",
      "A direct line to the team, with uptime and support terms",
    ],
    cta: { label: "Contact us", href: CONTACT },
  },
];

const faq: { q: string; a: string }[] = [
  {
    q: "Is this real money?",
    a: "No. Game Banking Engine simulates banking for in-game currencies only. It isn't a bank, holds no real money, and its card numbers work on no real payment network. Don't use it for balances players can cash out or exchange for real money.",
  },
  {
    q: "What does Pro add?",
    a: "Pro is for the dashboard, not the API: your game works the same on every plan. It starts with live balances, which shows player and bank balances changing as money moves, with no refreshing. More Pro features are on the way and are included as they ship. Start a 14-day free trial from your account page or from any Pro feature.",
  },
  {
    q: "What happens when the Pro trial ends?",
    a: "You go back to Free automatically. Nothing is deleted and your games keep running; only the Pro screens lock again. To keep Pro, subscribe from your account page: payment is by card through Stripe, and you can cancel any time.",
  },
  {
    q: "What happens when the beta ends?",
    a: "We'll publish paid plans at least 60 days before they apply, and tell every developer by email. Sandbox stays free. Your games, keys and ledger history carry over unchanged.",
  },
  {
    q: "Are there limits during the beta?",
    a: "Each API key has fair-use rate limits, with stricter ones on money movements and name enquiry. If a request is limited you get 429 with Retry-After, and every SDK waits and retries for you. Planning a big launch? Contact us first.",
  },
  {
    q: "Do I need a card to sign up?",
    a: "No. Create a developer account with an email and password and you get a sandbox straight away.",
  },
  {
    q: "Can I sell in-game currency to players?",
    a: "That's your game's business, and the engine can track what they buy. What it can't do is pay real money back out: once players can cash out or exchange balances for real money, you're running a regulated financial service, which this isn't.",
  },
];

export function Pricing() {
  return (
    <div className="landing">
      <SiteNav page="pricing" />
      <main>
        <section className="lp-hero lp-hero-short">
          <div className="lp-wrap">
            <span className="lp-eyebrow">Pricing</span>
            <h1>The engine is free. Pro adds live views.</h1>
            <p className="lp-lede">
              Build in the sandbox for free, forever, and run your live game for free during the beta. Pro adds dashboard
              extras such as live balances, with a 14-day free trial. Any change to the Free plan is announced at least
              60 days before it applies.
            </p>
          </div>
        </section>

        <section className="lp-section">
          <div className="lp-wrap">
            <div className="lp-plans">
              {plans.map((p) => (
                <article className={`lp-plan${p.featured ? " featured" : ""}`} key={p.name}>
                  {p.tag && <span className="lp-tag lp-plan-tag">{p.tag}</span>}
                  <h2>{p.name}</h2>
                  <p className="lp-plan-price">
                    <strong>{p.price}</strong>
                    {p.per && <span> {p.per}</span>}
                  </p>
                  <p className="lp-plan-blurb">{p.blurb}</p>
                  <ul>
                    {p.items.map((i) => <li key={i}>{i}</li>)}
                  </ul>
                  <a className={`btn lp-big ${p.featured ? "primary" : ""}`} href={p.cta.href}>{p.cta.label}</a>
                </article>
              ))}
            </div>
            <p className="lp-plan-note">
              Every plan is for in-game money only. See <a href="#simulated">what that means</a> and the{" "}
              <a href={docsHome()}>developer guide</a>.
            </p>
          </div>
        </section>

        <section className="lp-section lp-alt">
          <div className="lp-wrap">
            <div className="lp-head">
              <span className="lp-eyebrow">Questions</span>
              <h2>Pricing FAQ</h2>
            </div>
            <div className="lp-faq">
              {faq.map((f) => (
                <details key={f.q}>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <SimulatedMoney />
      </main>
      <SiteFooter />
    </div>
  );
}
